import {
  Building2,
  Calendar,
  Users,
  CreditCard,
  Briefcase,
  FileText,
  Settings,
  Paperclip,
  ChevronDown,
  Download,
  ExternalLink,
  Loader2,
  Landmark,
  UserCircle,
  MapPin,
} from 'lucide-react';
import {useState} from 'react';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {trpc} from '@common-web/lib/trpc';

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface Contact {
  firstName?: string;
  lastName?: string;
  title?: string;
  phone?: string;
  email?: string;
}

interface AchFormData {
  bankAccount?: {
    businessNameOnAccount?: string;
    accountHolderFirstName?: string;
    accountHolderLastName?: string;
    contactPhone?: string;
    contactEmail?: string;
    address?: Address;
  };
  achDetails?: {
    bankName?: string;
    bankAddress?: Address;
    routingNumber?: string;
    accountNumber?: string;
  };
  authorization?: {
    authorizedSignature?: string;
    signatureDate?: string;
  };
}

interface Location {
  locationName?: string;
  fein?: string;
  payrollLocationCode?: string;
  companyAddress?: Address;
  mailingAddress?: Address;
  contactPerson?: Contact;
  achForm?: AchFormData;
}

interface FormData {
  groupInfo?: {
    benefitYear?: string;
    companyName?: string;
    companyDba?: string;
    otherKnownName?: string;
    sic?: string;
    stateOfDoingBusiness?: string;
    totalEmployees?: number;
    numberOfLocations?: number;
    locations?: Location[];
  };
  admin?: {
    eligibility?: {fullTime?: boolean; partTime?: boolean};
    waitingPeriod?: number | null;
    payrollFrequencies?: string[];
    enrollmentCompany?: string;
    enrollmentType?: string;
    billingPreOrPostTax?: string;
    benefitsPreOrPostTax?: string;
  };
  contacts?: {
    authorizedSigner?: Contact;
    eligibilityContact?: Contact;
    billingContact?: Contact;
  };
  payrollInfo?: {
    contact?: Contact;
    vendor?: string;
    payrollVendorContact?: Contact;
    system?: string;
    payrollCompanyId?: string;
  };
  agentOfRecord?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    additionalPhone?: string;
    additionalEmail?: string;
    pepm?: string;
  };
}

interface ClientPortalAccessData {
  users?: Array<Contact>;
}

interface InternalData {
  groupNumber?: string;
  productCodes?: Array<{value: string}>;
  carrierName?: string;
  carrierNameOther?: string;
  telemedicine?: string;
  telemedicineOther?: string;
  pbm?: string;
  pbmOther?: string;
  app?: string;
  appOther?: string;
  mec?: string;
  mecOther?: string;
  ppoNetwork?: string;
  ppoNetworkOther?: string;
}

interface Attachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  contentType: string | null;
  s3Key: string;
  createdAt: Date | string;
}

interface Proposal {
  id: string;
  url: string;
  password: string;
  label: string | null;
  createdByName: string | null;
  createdAt: Date | string;
}

interface RfcSubmissionData {
  id: string;
  companyName: string | null;
  status: string;
  section125FormEnabled: boolean;
  section125FormData: unknown;
  mecFormEnabled: boolean;
  mecFormData: unknown;
  achFormData: unknown;
  internalData: unknown;
  clientPortalAccessData: unknown;
  msaDocuSignStatus: string | null;
  msaSignedDocumentS3Key: string | null;
  msaSignedAt: Date | string | null;
  msaSentAt: Date | string | null;
  submittedAt: Date | string | null;
  createdAt: Date | string;
  assignedBroker: {
    id: string;
    name: string;
    contactFirstName: string | null;
    contactLastName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
  } | null;
  attachments: Attachment[];
  proposals: Proposal[];
}

interface DealInformationPanelProps {
  rfcSubmission: RfcSubmissionData;
}

function formatAddress(addr: Address | undefined): string {
  if (!addr) return '—';
  const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function formatContact(contact: Contact | undefined): {name: string; details: string} {
  if (!contact) return {name: '—', details: ''};
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—';
  const details = [contact.title, contact.phone, contact.email].filter(Boolean).join(' · ');
  return {name, details};
}

function formatEnumLabel(value: string | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function maskAccountNumber(num: string | undefined): string {
  if (!num || num.length < 4) return '****';
  return '****' + num.slice(-4);
}

const msaStatusColors: Record<string, string> = {
  SENT: 'bg-blue-100 text-blue-700',
  DELIVERED: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  SIGNED: 'bg-emerald-100 text-emerald-700',
  DECLINED: 'bg-red-100 text-red-700',
  VOIDED: 'bg-gray-100 text-gray-700',
};

const rfcStatusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  CLIENT_REVIEW: 'bg-amber-100 text-amber-700',
  CLIENT_APPROVED: 'bg-blue-100 text-blue-700',
  CBS_PROCESSING: 'bg-blue-100 text-blue-700',
  CBS_PROCESSED: 'bg-blue-100 text-blue-700',
  BROKER_REVIEW: 'bg-amber-100 text-amber-700',
  BROKER_APPROVED: 'bg-blue-100 text-blue-700',
  MSA_PENDING: 'bg-amber-100 text-amber-700',
  MSA_SENT: 'bg-blue-100 text-blue-700',
  MSA_SIGNED: 'bg-emerald-100 text-emerald-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

function CollapsibleSection({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  badge,
}: {
  title: string;
  icon: React.ComponentType<{className?: string}>;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className='border rounded-lg'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors'
      >
        <div className='flex items-center gap-2'>
          <Icon className='h-4 w-4 text-gray-500' />
          <span className='text-sm font-semibold text-gray-900'>{title}</span>
          {badge}
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className='px-4 pb-4 border-t'>{children}</div>}
    </div>
  );
}

function InfoRow({label, value}: {label: string; value: string | React.ReactNode}) {
  return (
    <div className='flex justify-between py-1.5'>
      <span className='text-xs text-gray-500'>{label}</span>
      <span className='text-xs font-medium text-gray-900 text-right max-w-[60%]'>{value}</span>
    </div>
  );
}

function ContactCard({label, contact}: {label: string; contact: Contact | undefined}) {
  const {name, details} = formatContact(contact);
  if (name === '—') return null;
  return (
    <div className='bg-gray-50 rounded-md p-3'>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p className='text-sm font-medium text-gray-900'>{name}</p>
      {details && <p className='text-xs text-gray-500 mt-0.5'>{details}</p>}
    </div>
  );
}

function getInternalDisplayValue(value: string, otherValue: string | undefined): string {
  if (value === 'OTHER' && otherValue) return otherValue;
  return formatEnumLabel(value);
}

function AttachmentDownloadButton({attachmentId}: {attachmentId: string}) {
  const downloadMutation = trpc.internal.onboarding.downloadRfcAttachment.useMutation({
    onSuccess: (data) => {
      window.open(data.downloadUrl, '_blank');
    },
  });

  return (
    <Button
      variant='ghost'
      size='icon'
      className='h-7 w-7'
      onClick={(e) => {
        e.stopPropagation();
        downloadMutation.mutate({attachmentId});
      }}
      disabled={downloadMutation.isPending}
    >
      {downloadMutation.isPending ? (
        <Loader2 className='h-3.5 w-3.5 animate-spin text-gray-400' />
      ) : (
        <Download className='h-3.5 w-3.5 text-brand-blue' />
      )}
    </Button>
  );
}

function MsaDownloadButton({rfcSubmissionId}: {rfcSubmissionId: string}) {
  const downloadMutation = trpc.internal.onboarding.downloadMsaDocument.useMutation({
    onSuccess: (data) => {
      window.open(data.downloadUrl, '_blank');
    },
  });

  return (
    <Button
      variant='outline'
      size='sm'
      className='h-7 text-xs'
      onClick={() => downloadMutation.mutate({rfcSubmissionId})}
      disabled={downloadMutation.isPending}
    >
      {downloadMutation.isPending ? (
        <Loader2 className='h-3 w-3 animate-spin mr-1' />
      ) : (
        <Download className='h-3 w-3 mr-1' />
      )}
      Download MSA
    </Button>
  );
}

export function DealInformationPanel({rfcSubmission}: DealInformationPanelProps) {
  const formData = (
    rfcSubmission.section125FormEnabled
      ? rfcSubmission.section125FormData
      : rfcSubmission.mecFormData
  ) as FormData | null;

  const achData = rfcSubmission.achFormData as AchFormData | null;
  const internalData = rfcSubmission.internalData as InternalData | null;
  const clientPortalData = rfcSubmission.clientPortalAccessData as ClientPortalAccessData | null;
  const hasPerLocationAch =
    formData?.groupInfo?.locations?.some(
      (loc) =>
        loc.achForm && (loc.achForm.achDetails?.bankName || loc.achForm.achDetails?.routingNumber),
    ) ?? false;

  return (
    <div className='space-y-3'>
      {/* Deal Status Banner */}
      <div className='flex items-center gap-2 flex-wrap'>
        <Badge
          className={`text-xs ${rfcStatusColors[rfcSubmission.status] ?? 'bg-gray-100 text-gray-700'}`}
        >
          Deal: {formatEnumLabel(rfcSubmission.status)}
        </Badge>
        {rfcSubmission.msaDocuSignStatus && (
          <Badge
            className={`text-xs ${msaStatusColors[rfcSubmission.msaDocuSignStatus] ?? 'bg-gray-100 text-gray-700'}`}
          >
            MSA: {rfcSubmission.msaDocuSignStatus}
          </Badge>
        )}
        {rfcSubmission.section125FormEnabled && (
          <Badge variant='outline' className='text-xs'>
            Section 125
          </Badge>
        )}
        {rfcSubmission.mecFormEnabled && (
          <Badge variant='outline' className='text-xs'>
            MEC
          </Badge>
        )}
      </div>

      {/* Timeline */}
      <div className='flex items-center gap-4 text-xs text-gray-500 px-1'>
        {rfcSubmission.createdAt && <span>Created {formatDate(rfcSubmission.createdAt)}</span>}
        {rfcSubmission.submittedAt && (
          <span>Submitted {formatDate(rfcSubmission.submittedAt)}</span>
        )}
        {rfcSubmission.msaSignedAt && (
          <span>MSA Signed {formatDate(rfcSubmission.msaSignedAt)}</span>
        )}
      </div>

      {/* Company Information */}
      {formData?.groupInfo && (
        <CollapsibleSection title='Company Information' icon={Building2} defaultOpen>
          <div className='mt-3 space-y-1'>
            <InfoRow label='Company Name' value={formData.groupInfo.companyName || '—'} />
            {formData.groupInfo.companyDba && (
              <InfoRow label='DBA' value={formData.groupInfo.companyDba} />
            )}
            {formData.groupInfo.otherKnownName && (
              <InfoRow label='Other Known Name' value={formData.groupInfo.otherKnownName} />
            )}
            {formData.groupInfo.sic && <InfoRow label='SIC Code' value={formData.groupInfo.sic} />}
            {formData.groupInfo.stateOfDoingBusiness && (
              <InfoRow
                label='Primary State of Doing Business'
                value={formData.groupInfo.stateOfDoingBusiness}
              />
            )}
            <InfoRow
              label='Total Employees'
              value={String(formData.groupInfo.totalEmployees ?? '—')}
            />
            <InfoRow
              label='Locations'
              value={String(
                formData.groupInfo.numberOfLocations ?? formData.groupInfo.locations?.length ?? '—',
              )}
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Benefit & Admin Details */}
      {formData?.groupInfo && (
        <CollapsibleSection title='Benefit & Admin Details' icon={Calendar} defaultOpen>
          <div className='mt-3 space-y-1'>
            <InfoRow label='Benefit Year' value={formData.groupInfo.benefitYear || '—'} />
            {formData.admin && (
              <>
                <InfoRow
                  label='Eligibility'
                  value={
                    [
                      formData.admin.eligibility?.fullTime && 'Full-Time',
                      formData.admin.eligibility?.partTime && 'Part-Time',
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'
                  }
                />
                <InfoRow
                  label='Waiting Period for Newly Hired Employees'
                  value={
                    formData.admin.waitingPeriod != null
                      ? `${formData.admin.waitingPeriod} days`
                      : '—'
                  }
                />
                <InfoRow
                  label='Payroll Frequency'
                  value={formData.admin.payrollFrequencies?.map(formatEnumLabel).join(', ') || '—'}
                />
                {formData.admin.enrollmentCompany && (
                  <InfoRow label='Enrollment Company' value={formData.admin.enrollmentCompany} />
                )}
                <InfoRow
                  label='Enrollment Type'
                  value={formatEnumLabel(formData.admin.enrollmentType)}
                />
                <InfoRow
                  label='Premiums Tax Treatment'
                  value={formatEnumLabel(formData.admin.billingPreOrPostTax)}
                />
                <InfoRow
                  label='Benefits Tax Treatment'
                  value={formatEnumLabel(formData.admin.benefitsPreOrPostTax)}
                />
              </>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Contacts */}
      {formData?.contacts && (
        <CollapsibleSection title='Contacts' icon={Users} defaultOpen>
          <div className='mt-3 space-y-2'>
            <ContactCard label='Authorized Signer' contact={formData.contacts.authorizedSigner} />
            <ContactCard
              label='Eligibility Contact'
              contact={formData.contacts.eligibilityContact}
            />
            <ContactCard label='Billing Contact' contact={formData.contacts.billingContact} />
          </div>
        </CollapsibleSection>
      )}

      {/* Payroll */}
      {formData?.payrollInfo && (
        <CollapsibleSection title='Payroll Information' icon={CreditCard}>
          <div className='mt-3 space-y-1'>
            <InfoRow label='Vendor' value={formData.payrollInfo.vendor || '—'} />
            <InfoRow label='System' value={formData.payrollInfo.system || '—'} />
            {formData.payrollInfo.payrollCompanyId && (
              <InfoRow label='Payroll Company ID' value={formData.payrollInfo.payrollCompanyId} />
            )}
          </div>
          <div className='mt-2 space-y-2'>
            <ContactCard label='Internal Payroll Contact' contact={formData.payrollInfo.contact} />
            <ContactCard
              label='Payroll Vendor Contact'
              contact={formData.payrollInfo.payrollVendorContact}
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Agent of Record / Broker */}
      {(formData?.agentOfRecord || rfcSubmission.assignedBroker) && (
        <CollapsibleSection title='Agent of Record / Broker' icon={Briefcase} defaultOpen>
          <div className='mt-3 space-y-1'>
            {rfcSubmission.assignedBroker && (
              <div className='bg-blue-50 rounded-md p-3 mb-2'>
                <p className='text-xs text-blue-600 font-medium mb-1'>Assigned Broker</p>
                <p className='text-sm font-medium text-gray-900'>
                  {rfcSubmission.assignedBroker.name}
                </p>
                <div className='mt-1 space-y-0.5'>
                  {(rfcSubmission.assignedBroker.contactFirstName ||
                    rfcSubmission.assignedBroker.contactLastName) && (
                    <p className='text-xs text-gray-600'>
                      {[
                        rfcSubmission.assignedBroker.contactFirstName,
                        rfcSubmission.assignedBroker.contactLastName,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </p>
                  )}
                  {rfcSubmission.assignedBroker.contactEmail && (
                    <p className='text-xs text-gray-500'>
                      {rfcSubmission.assignedBroker.contactEmail}
                    </p>
                  )}
                  {rfcSubmission.assignedBroker.contactPhone && (
                    <p className='text-xs text-gray-500'>
                      {rfcSubmission.assignedBroker.contactPhone}
                    </p>
                  )}
                </div>
              </div>
            )}
            {formData?.agentOfRecord && (
              <>
                <InfoRow
                  label='Agent Name'
                  value={
                    [formData.agentOfRecord.firstName, formData.agentOfRecord.lastName]
                      .filter(Boolean)
                      .join(' ') || '—'
                  }
                />
                {formData.agentOfRecord.email && (
                  <InfoRow label='Agent Email' value={formData.agentOfRecord.email} />
                )}
                {formData.agentOfRecord.phone && (
                  <InfoRow label='Agent Phone' value={formData.agentOfRecord.phone} />
                )}
                {formData.agentOfRecord.additionalEmail && (
                  <InfoRow
                    label='Additional Email'
                    value={formData.agentOfRecord.additionalEmail}
                  />
                )}
                {formData.agentOfRecord.additionalPhone && (
                  <InfoRow
                    label='Additional Phone'
                    value={formData.agentOfRecord.additionalPhone}
                  />
                )}
                {formData.agentOfRecord.pepm && (
                  <InfoRow label='PEPM' value={formData.agentOfRecord.pepm} />
                )}
              </>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Internal Details */}
      {internalData && (
        <CollapsibleSection title='Internal Details' icon={Settings} defaultOpen>
          <div className='mt-3 space-y-1'>
            {internalData.groupNumber && (
              <InfoRow label='Group Number' value={internalData.groupNumber} />
            )}
            {internalData.carrierName && (
              <InfoRow
                label='Carrier'
                value={getInternalDisplayValue(
                  internalData.carrierName,
                  internalData.carrierNameOther,
                )}
              />
            )}
            {internalData.mec && (
              <InfoRow
                label='MEC'
                value={getInternalDisplayValue(internalData.mec, internalData.mecOther)}
              />
            )}
            {internalData.telemedicine && (
              <InfoRow
                label='Telemedicine'
                value={getInternalDisplayValue(
                  internalData.telemedicine,
                  internalData.telemedicineOther,
                )}
              />
            )}
            {internalData.pbm && (
              <InfoRow
                label='PBM'
                value={getInternalDisplayValue(internalData.pbm, internalData.pbmOther)}
              />
            )}
            {internalData.ppoNetwork && (
              <InfoRow
                label='PPO Network'
                value={getInternalDisplayValue(
                  internalData.ppoNetwork,
                  internalData.ppoNetworkOther,
                )}
              />
            )}
            {internalData.app && (
              <InfoRow
                label='App'
                value={getInternalDisplayValue(internalData.app, internalData.appOther)}
              />
            )}
            {internalData.productCodes && internalData.productCodes.length > 0 && (
              <InfoRow
                label='Product Codes'
                value={internalData.productCodes.map((c) => c.value).join(', ')}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* ACH / Banking (legacy fallback - only show if top-level ACH exists and no per-location ACH) */}
      {achData && (achData.bankAccount || achData.achDetails) && !hasPerLocationAch && (
        <CollapsibleSection title='ACH / Banking' icon={Landmark}>
          <div className='mt-3 space-y-1'>
            {achData.bankAccount && (
              <>
                {achData.bankAccount.businessNameOnAccount && (
                  <InfoRow
                    label='Business Name'
                    value={achData.bankAccount.businessNameOnAccount}
                  />
                )}
                {(achData.bankAccount.accountHolderFirstName ||
                  achData.bankAccount.accountHolderLastName) && (
                  <InfoRow
                    label='Account Holder'
                    value={[
                      achData.bankAccount.accountHolderFirstName,
                      achData.bankAccount.accountHolderLastName,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                )}
                {achData.bankAccount.contactPhone && (
                  <InfoRow label='Contact Phone' value={achData.bankAccount.contactPhone} />
                )}
                {achData.bankAccount.contactEmail && (
                  <InfoRow label='Contact Email' value={achData.bankAccount.contactEmail} />
                )}
                {achData.bankAccount.address?.street && (
                  <InfoRow
                    label='Account Address'
                    value={formatAddress(achData.bankAccount.address)}
                  />
                )}
              </>
            )}
            {achData.achDetails && (
              <>
                {achData.achDetails.bankName && (
                  <InfoRow label='Bank Name' value={achData.achDetails.bankName} />
                )}
                {achData.achDetails.bankAddress?.street && (
                  <InfoRow
                    label='Bank Address'
                    value={formatAddress(achData.achDetails.bankAddress)}
                  />
                )}
                {achData.achDetails.routingNumber && (
                  <InfoRow label='Routing Number' value={achData.achDetails.routingNumber} />
                )}
                {achData.achDetails.accountNumber && (
                  <InfoRow
                    label='Account Number'
                    value={maskAccountNumber(achData.achDetails.accountNumber)}
                  />
                )}
              </>
            )}
            {achData.authorization && (
              <>
                {achData.authorization.authorizedSignature && (
                  <InfoRow
                    label='Authorized Signature'
                    value={achData.authorization.authorizedSignature}
                  />
                )}
                {achData.authorization.signatureDate && (
                  <InfoRow label='Signature Date' value={achData.authorization.signatureDate} />
                )}
              </>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Client Portal Access */}
      {clientPortalData?.users && clientPortalData.users.length > 0 && (
        <CollapsibleSection
          title='Client Portal Access'
          icon={UserCircle}
          badge={
            <Badge variant='outline' className='text-xs'>
              {clientPortalData.users.length} user{clientPortalData.users.length !== 1 ? 's' : ''}
            </Badge>
          }
        >
          <div className='mt-3 space-y-2'>
            {clientPortalData.users.map((user, idx) => (
              <ContactCard key={idx} label={`User ${idx + 1}`} contact={user} />
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* MSA Details */}
      {rfcSubmission.msaDocuSignStatus && (
        <CollapsibleSection title='MSA Agreement' icon={FileText} defaultOpen>
          <div className='mt-3 space-y-1'>
            <InfoRow
              label='Status'
              value={
                <Badge
                  className={`text-xs ${msaStatusColors[rfcSubmission.msaDocuSignStatus] ?? 'bg-gray-100 text-gray-700'}`}
                >
                  {rfcSubmission.msaDocuSignStatus}
                </Badge>
              }
            />
            {rfcSubmission.msaSentAt && (
              <InfoRow label='Sent' value={formatDate(rfcSubmission.msaSentAt)} />
            )}
            {rfcSubmission.msaSignedAt && (
              <InfoRow label='Signed' value={formatDate(rfcSubmission.msaSignedAt)} />
            )}
            {rfcSubmission.msaSignedDocumentS3Key && (
              <div className='mt-2'>
                <MsaDownloadButton rfcSubmissionId={rfcSubmission.id} />
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Attachments */}
      {rfcSubmission.attachments.length > 0 && (
        <CollapsibleSection
          title='Attachments'
          icon={Paperclip}
          badge={
            <Badge variant='outline' className='text-xs'>
              {rfcSubmission.attachments.length}
            </Badge>
          }
          defaultOpen
        >
          <div className='mt-3 space-y-2'>
            {rfcSubmission.attachments.map((att) => (
              <div
                key={att.id}
                className='flex items-center justify-between bg-gray-50 rounded-md px-3 py-2'
              >
                <div className='min-w-0 flex-1'>
                  <p className='text-xs font-medium text-gray-900 truncate'>{att.fileName}</p>
                  <p className='text-xs text-gray-500'>
                    {formatEnumLabel(att.fileType)} · {formatFileSize(att.fileSize)} ·{' '}
                    {formatDate(att.createdAt)}
                  </p>
                </div>
                <AttachmentDownloadButton attachmentId={att.id} />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Proposals */}
      {rfcSubmission.proposals.length > 0 && (
        <CollapsibleSection
          title='Proposals'
          icon={FileText}
          badge={
            <Badge variant='outline' className='text-xs'>
              {rfcSubmission.proposals.length}
            </Badge>
          }
          defaultOpen
        >
          <div className='mt-3 space-y-2'>
            {rfcSubmission.proposals.map((prop) => (
              <div key={prop.id} className='bg-gray-50 rounded-md px-3 py-2'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-xs font-medium text-gray-900'>{prop.label || 'Proposal'}</p>
                    <p className='text-xs text-gray-500'>
                      by {prop.createdByName || 'Unknown'} · {formatDate(prop.createdAt)}
                    </p>
                  </div>
                  <a
                    href={prop.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-xs text-brand-blue hover:underline'
                  >
                    Open <ExternalLink className='h-3 w-3' />
                  </a>
                </div>
                {prop.password && (
                  <p className='text-xs text-gray-400 mt-1'>
                    Password:{' '}
                    <code className='bg-gray-200 px-1 rounded-sm text-gray-600'>
                      {prop.password}
                    </code>
                  </p>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Locations */}
      {formData?.groupInfo?.locations && formData.groupInfo.locations.length > 0 && (
        <CollapsibleSection
          title='Locations'
          icon={MapPin}
          badge={
            <Badge variant='outline' className='text-xs'>
              {formData.groupInfo.locations.length}
            </Badge>
          }
        >
          <div className='mt-3 space-y-3'>
            {formData.groupInfo.locations.map((loc, idx) => (
              <div key={idx} className='bg-gray-50 rounded-md p-3'>
                <p className='text-sm font-medium text-gray-900'>
                  {loc.locationName || `Location ${idx + 1}`}
                </p>
                {loc.fein && <p className='text-xs text-gray-500'>FEIN: {loc.fein}</p>}
                {loc.payrollLocationCode && (
                  <p className='text-xs text-gray-500'>
                    Payroll Location Code: {loc.payrollLocationCode}
                  </p>
                )}
                {loc.companyAddress?.street && (
                  <p className='text-xs text-gray-500 mt-0.5'>
                    Address: {formatAddress(loc.companyAddress)}
                  </p>
                )}
                {loc.mailingAddress?.street && (
                  <p className='text-xs text-gray-500'>
                    Mailing: {formatAddress(loc.mailingAddress)}
                  </p>
                )}
                {loc.contactPerson &&
                  (loc.contactPerson.firstName || loc.contactPerson.lastName) && (
                    <p className='text-xs text-gray-500 mt-1'>
                      Contact:{' '}
                      {[loc.contactPerson.firstName, loc.contactPerson.lastName]
                        .filter(Boolean)
                        .join(' ')}
                      {loc.contactPerson.phone && ` · ${loc.contactPerson.phone}`}
                      {loc.contactPerson.email && ` · ${loc.contactPerson.email}`}
                    </p>
                  )}
                {loc.achForm &&
                  (loc.achForm.achDetails?.bankName || loc.achForm.achDetails?.routingNumber) && (
                    <div className='mt-2 pt-2 border-t border-gray-200 space-y-1'>
                      <p className='text-xs font-medium text-gray-700'>ACH / Banking</p>
                      {loc.achForm.bankAccount?.businessNameOnAccount && (
                        <InfoRow
                          label='Business Name'
                          value={loc.achForm.bankAccount.businessNameOnAccount}
                        />
                      )}
                      {(loc.achForm.bankAccount?.accountHolderFirstName ||
                        loc.achForm.bankAccount?.accountHolderLastName) && (
                        <InfoRow
                          label='Account Holder'
                          value={[
                            loc.achForm.bankAccount?.accountHolderFirstName,
                            loc.achForm.bankAccount?.accountHolderLastName,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        />
                      )}
                      {loc.achForm.bankAccount?.contactPhone && (
                        <InfoRow
                          label='Contact Phone'
                          value={loc.achForm.bankAccount.contactPhone}
                        />
                      )}
                      {loc.achForm.bankAccount?.contactEmail && (
                        <InfoRow
                          label='Contact Email'
                          value={loc.achForm.bankAccount.contactEmail}
                        />
                      )}
                      {loc.achForm.bankAccount?.address?.street && (
                        <InfoRow
                          label='Account Address'
                          value={formatAddress(loc.achForm.bankAccount.address)}
                        />
                      )}
                      {loc.achForm.achDetails?.bankName && (
                        <InfoRow label='Bank Name' value={loc.achForm.achDetails.bankName} />
                      )}
                      {loc.achForm.achDetails?.bankAddress?.street && (
                        <InfoRow
                          label='Bank Address'
                          value={formatAddress(loc.achForm.achDetails.bankAddress)}
                        />
                      )}
                      {loc.achForm.achDetails?.routingNumber && (
                        <InfoRow
                          label='Routing Number'
                          value={loc.achForm.achDetails.routingNumber}
                        />
                      )}
                      {loc.achForm.achDetails?.accountNumber && (
                        <InfoRow
                          label='Account Number'
                          value={maskAccountNumber(loc.achForm.achDetails.accountNumber)}
                        />
                      )}
                      {loc.achForm.authorization?.authorizedSignature && (
                        <InfoRow
                          label='Authorized Signature'
                          value={loc.achForm.authorization.authorizedSignature}
                        />
                      )}
                      {loc.achForm.authorization?.signatureDate && (
                        <InfoRow
                          label='Signature Date'
                          value={loc.achForm.authorization.signatureDate}
                        />
                      )}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
